import React, { Component } from "react";
import { Tooltip } from "@material-ui/core";
import { Icon, MenuItem } from "@material-ui/core";
import styled from "styled-components";

import { Gap5, Gap10 } from "js/react/components/Gap";
import { ThumbnailCard, thumbnailMenuButtonStyles } from "js/react/views/AddSlide/Panes/Components/ThumbnailCard";
import Thumbnails from "js/core/models/thumbnails";
import { ds } from "js/core/models/dataService";
import { IconMenu } from "js/react/components/UiComponents";
import { THUMBNAIL_SIZES } from "common/constants";
import getLogger, { LogGroup } from "js/core/logger";
import getUserProfile from "js/core/services/userProfiles";
import WorkspaceController from "js/controllers/WorkspaceController";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import { Divider } from "js/Components/Divider";
import { t } from "js/i18n";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { getStaticUrl } from "js/config";

import { Thumbnail } from "./ThumbnailGrid";

const logger = getLogger(LogGroup.THUMBNAIL);

const arrowShapeUpUrl = getStaticUrl("/images/ui/ornaments/arrow-shape-up.svg");

const ArrowShapeUp = styled.div.attrs(({ $fill, $width = 15, $height = 15 }) => ({
    style: {
        width: `${$width}px`,
        height: `${$height}px`,
        backgroundColor: $fill,
    }
}))`
    display: inline-block;
    -webkit-mask: ${({ $iconUrl }) => `url("${$iconUrl}")`} center / contain no-repeat;
    mask: ${({ $iconUrl }) => `url("${$iconUrl}")`} center / contain no-repeat;
`;

const TeamSlideThumbnailBar = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  label {
    flex-grow: 2;
    text-align: left;
    margin: 0px 10px 0px 0px;
  }
  .MuiIcon-root {
    margin-right: 3px;
    font-size: 18px;
  }
  & > div {
    font-size: 12px;
  }
  button {
    padding: 2px;
  }
`;

const ThumbnailContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
    padding: 9px 10px 10px 10px;
`;

const ThumbnailMetaRow = styled.div`
    display: flex;
    align-items: center;
    font-size: 10px;
    line-height: 19px;
    color: #666;
`;

const PresentationCountRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    line-height: 16px;

    .MuiIcon-root {
        font-size: 16px;
        color: #222;
    }
`;

const ThumbnailMenuWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const StyledIconMenu = styled(IconMenu)`
    &&& {
        ${thumbnailMenuButtonStyles}
    }
`;

const LikeButtonWrap = styled.div`
    ${thumbnailMenuButtonStyles}
    display: flex;
    align-items: center;
    gap: 4px;
    line-height: 125%;
    letter-spacing: -0.32px;
    cursor: pointer;
    color: #666;
`;

const DisabledSlideIcon = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 30px;
    height: 30px;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;

    span {
        color: white;
        font-size: 20px;
    }
`;

export const TeamSlideThumbnail = withFirebaseUser(
    class TeamSlideThumbnail extends Component {
        constructor() {
            super();

            this.state = {
                url: "",
                isVisible: false,
                isHovering: false,
                ItemInfo: null
            };

            this.ref = React.createRef();
        }

        componentDidMount() {
            this.loadItemInfo();

            this.observer = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting === true) {
                    this.observer.disconnect();
                    this.setState({ isVisible: true });
                }
            });

            this.observer.observe(this.ref.current);
        }

        componentDidUpdate(prevProps, prevState, snapshot) {
            const { item, onVisibleChange } = this.props;
            const { isVisible } = this.state;

            if (prevState.isVisible !== isVisible) {
                onVisibleChange?.(isVisible, item);
            }

            if ((prevState.isVisible !== isVisible && isVisible) || (prevProps.item.id !== item.id) || (prevProps.item.contentModifiedAt !== item.contentModifiedAt)) {
                this.loadThumbnail()
                    .catch(err => logger.error(err, "[TeamSlideThumbnail] this.loadThumbnail() failed", { id: item.id }));
            }

            if (item.description !== prevProps.item.description) {
                this.loadItemInfo();
            }
        }

        componentWillUnmount() {
            this.isUnmounted = true;
            this.observer.disconnect();
        }

        async loadThumbnail() {
            const { item, highQuality, showThemeThumbnails = true } = this.props;
            let url = "";
            this.setState({
                url
            });

            if (showThemeThumbnails && ds.selection.presentation && !ds.selection.presentation.get("isDummy")) {
                const presentationId = ds.selection.presentation.id;
                const firstSlideModifiedAt = ds.selection.presentation.get("firstSlideModifiedAt");
                url = await Thumbnails.getSignedUrlAndLoad(item.id, firstSlideModifiedAt, presentationId, THUMBNAIL_SIZES[highQuality ? "large" : "small"].suffix);
            } else {
                url = await Thumbnails.getSignedUrlAndLoad(item.id, item.contentModifiedAt);
            }

            if (!this.isUnmounted) {
                this.setState({ url });
            }
        }

        handleLikeItem = async () => {
            const { item: { resource }, firebaseUser } = this.props;

            let ratedByUsers = resource.metadata?.ratedByUsers ?? [];
            if (!ratedByUsers.includes(firebaseUser.uid)) {
                ratedByUsers.push(firebaseUser.uid);
            } else {
                ratedByUsers = ratedByUsers.filter(uid => uid !== firebaseUser.uid);
            }

            try {
                await WorkspaceController.updateWorkspaceResource({
                    resourceId: resource.resourceId,
                    resourceType: resource.resourceType,
                    metadata: {
                        ratedByUsers
                    }
                });
            } catch (err) {
                logger.error(err, "[TeamSlideThumbnail] handleLikeItem() failed", { id: resource.resourceId });
            }
        }

        async loadItemInfo() {
            const { item } = this.props;

            try {
                const profile = await getUserProfile(item.createdBy);
                const description = item.description;
                const ItemInfo = (
                    <div style={{ padding: 10 }}>
                        {description && <div>{description}</div>}
                        {description && <Gap10 />}
                        <div>{t("Created by")} <strong>{profile.displayName || profile.email || t("Unknown")}</strong></div>
                    </div>
                );

                this.setState({
                    ItemInfo: <div>{t("Created by")} {profile.displayName || profile.email || t("Unknown")}</div>

                });
            } catch (err) {
                logger.error(err, "[TeamSlideThumbnail] this.loadItemInfo() failed", { id: item.id });
            }
        }

        render() {
            const { item, onDoubleClick, selected, isManageLibrary, canUpgradeToManageSharedResources, onEditSlide, onDeleteSlide, onCopySlide, onDuplicateSlide, onMouseDown, onMouseUp, onContextMenu } = this.props;
            const { url, ItemInfo, isHovering } = this.state;

            let clickProps;
            if (isManageLibrary) {
                clickProps = {
                    onDoubleClick: onDoubleClick
                };
            } else {
                clickProps = {
                    onDoubleClick: onDoubleClick,
                    ...(onMouseDown ? { onMouseDown: onMouseDown } : { onClick: onDoubleClick }),
                };
            }

            const { isTemplate } = item;

            return (
                <ThumbnailCard
                    ref={this.ref}
                    onMouseDown={onMouseDown}
                    onMouseUp={onMouseUp}
                    selected={selected}
                    onContextMenu={onContextMenu}
                    onMouseEnter={() => this.setState({ isHovering: true })}
                    onMouseLeave={() => this.setState({ isHovering: false })}
                    menuOverlay={isHovering && isManageLibrary && (
                        <ThumbnailMenuWrap>
                            <Tooltip title={t("Upvote template to let your team know it's useful")}>
                                <LikeButtonWrap onClick={this.handleLikeItem}>
                                    <ArrowShapeUp $fill="#666" $width={16} $height={16} $iconUrl={arrowShapeUpUrl} />
                                    <div>{item.ratedByCount}</div>
                                </LikeButtonWrap>
                            </Tooltip>
                            <StyledIconMenu handleCloseMenu={() => this.setState({ isHovering: false })} fontSize="medium">
                                {canUpgradeToManageSharedResources ? [
                                    <MenuItem key="edit" disabled divider><UpgradeIcon /><Gap5 />{t("Edit Slide...")}</MenuItem>,
                                    <MenuItem key="properties" disabled divider><UpgradeIcon /><Gap5 />{t("View Properties...")}</MenuItem>,
                                    <MenuItem key="copy" disabled divider><UpgradeIcon /><Gap5 />{isTemplate ? t("Copy to Shared Slides...") : t("Copy to Slide Templates...")}</MenuItem>,
                                    <MenuItem key="duplicate" disabled divider><UpgradeIcon /><Gap5 />{t("Duplicate")}</MenuItem>,
                                ] : [
                                    <MenuItem key="edit" divider onClick={() => onEditSlide(item)}><Icon>edit</Icon>{t("Edit Slide...")}</MenuItem>,
                                    <MenuItem key="properties" divider onClick={onDoubleClick}><Icon>label</Icon>{t("View Properties...")}</MenuItem>,
                                    <MenuItem key="copy" divider onClick={() => onCopySlide(item)}>
                                        <Icon>change_circle</Icon>
                                        {isTemplate ? t("Copy to Shared Slides...") : t("Copy to Slide Templates...")}
                                    </MenuItem>,
                                    <MenuItem key="duplicate" divider onClick={() => onDuplicateSlide(item)}><Icon>file_copy</Icon>{t("Duplicate")}</MenuItem>,
                                ]}
                                <MenuItem key="delete" onClick={() => onDeleteSlide(item)}><Icon>delete</Icon>{t("Delete")}</MenuItem>

                            </StyledIconMenu>
                        </ThumbnailMenuWrap>
                    )}
                    barVariant="custom"
                    barContent={
                        <ThumbnailContent>
                            <TeamSlideThumbnailBar>
                                <label>{item.name}</label>
                            </TeamSlideThumbnailBar>
                            <ThumbnailMetaRow>
                                {ItemInfo && <span>{ItemInfo}</span>}
                                {ItemInfo && !isTemplate && <Divider vertical margin={8} style={{ background: "#DDD", borderRadius: "100px" }} />}
                                {!isTemplate &&
                                    <PresentationCountRow>
                                        <Icon>cloud_download</Icon>
                                        <div>{item.presentationCount}</div>
                                    </PresentationCountRow>
                                }
                            </ThumbnailMetaRow>
                        </ThumbnailContent>
                    }
                >
                    <Thumbnail
                        readOnly
                        {...clickProps}
                        url={url} showSpinner={true}
                        style={canUpgradeToManageSharedResources ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                        imageStyle={item.isDisabled ? {
                            filter: "brightness(0.7)",   // darken the image              // fade it a bit
                        } : {}}
                    >
                        {item.isDisabled && <DisabledSlideIcon><Icon>visibility_off</Icon></DisabledSlideIcon>}
                    </Thumbnail>
                </ThumbnailCard>
            );
        }
    }
);
